import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';

import {
  ThreeJSClass,
  ThreeJSRotation,
  easeInOutSine,
} from '@threeJS/threejs.helper';

import Simulator from '../simulator';

export default class Resources extends ThreeJSClass {
  _model?: THREE.Group | THREE.Mesh;
  _material: THREE.MeshPhongMaterial;
  _bbox?: THREE.Box3Helper;
  _initialRotation: ThreeJSRotation;

  constructor({
    simulation,
    resource,
    scale,
    initialRotation,
    onLoad,
  }: {
    simulation: Simulator;
    resource: string;
    scale: number;
    initialRotation: ThreeJSRotation;
    onLoad: (model: THREE.Group | THREE.Mesh) => void;
  }) {
    super(simulation);
    this._initialRotation = initialRotation;
    this._material = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      // specular: resourceColour,
      shininess: 0,
    });

    const loadingManager = new THREE.LoadingManager(() => {
      if (this._model) onLoad(this._model);
    });

    const dracoLoader = new DRACOLoader(loadingManager);
    dracoLoader.setDecoderConfig({ type: 'js' });
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');

    const gltfLoader = new GLTFLoader(loadingManager);
    gltfLoader.setDRACOLoader(dracoLoader);

    gltfLoader.load(
      `${window.location.origin}/assets/${resource}`,
      this.onGTLFLoded(scale),
      xhr => {
        console.log((xhr.loaded / xhr.total) * 100 + '% loaded');
      },
      error => {
        console.log('error');
        console.log(error);
      }
    );
    // const stlLoader = new STLLoader(loadingManager);

    // stlLoader.load(
    //   `${window.location.origin}/assets/${resource}`,
    //   this.onSTLLoaded(initialRotation, scale),
    //   xhr => {
    //     console.log((xhr.loaded / xhr.total) * 100 + '% loaded');
    //   },
    //   error => {
    //     console.log(error);
    //   }
    // );
  }

  onGTLFLoded = (scale: number) => (gltf: GLTF) => {
    const obj = new THREE.Group();
    const offset = new THREE.Vector3(0, 0, 0);
    obj.scale.set(0.25 * scale, 0.25 * scale, 0.25 * scale);
    while (gltf.scene.children.length) {
      gltf.scene.children[0].position.add(offset);
      gltf.scene.children[0].castShadow = true;
      gltf.scene.children[0].receiveShadow = false;
      obj.add(gltf.scene.children[0]);
    }
    obj.children[0].receiveShadow = false;
    obj.children[0].castShadow = false;
    this._model = obj;
  };

  onSTLLoaded =
    (initialRotation: ThreeJSRotation, scale: number) =>
    (stl: THREE.BufferGeometry) => {
      stl.center();
      stl.rotateX(initialRotation.x);
      stl.rotateY(initialRotation.y);
      stl.rotateZ(initialRotation.z);
      const mesh = new THREE.Mesh(stl, this._material);
      mesh.rotation.set(0, 0, -Math.PI);
      mesh.scale.set(0.1 * scale, 0.1 * scale, 0.1 * scale);
      mesh.receiveShadow = true;
      mesh.castShadow = true;
      this.alignToGround(mesh);
      this._model = mesh;
    };

  alignToGround = (model: THREE.Object3D) => {
    const size = this.getSize(model);
    model.position.set(0, size.y / 2, 0);
    this.addBBox(model);
  };

  addBBox = (model: THREE.Object3D) => {
    let bbox = new THREE.Box3().setFromObject(model);
    this._bbox = new THREE.Box3Helper(bbox, new THREE.Color(0, 255, 0));
    this._simulation.scene.add(this._bbox);
    this._bbox.visible = this._simulation.debug;
  };

  getSize = (model: THREE.Object3D) => {
    let bbox = new THREE.Box3().setFromObject(model);
    return bbox.getSize(new THREE.Vector3());
  };

  get material() {
    return this._material;
  }

  onTick(elapsedTime: number): void {
    // console.log('tick', this._simulation.autoRotate, this._initialRotation);
    // if (this._simulation._autoRotate && this._model) {
    //   this._model.rotation.y = elapsedTime * 0.3;
    // }

    if (this._simulation.autoRotate && this._model) {
      if (this._simulation._flying) {
        this._model.rotation.y =
          this._initialRotation.y - easeInOutSine(elapsedTime * 0.2);
        this._model.rotation.x =
          this._initialRotation.x - easeInOutSine(elapsedTime * 0.1);
      } else {
        this._model.rotation.y =
          this._initialRotation.y - easeInOutSine(elapsedTime * 0.4);
        this._model.rotation.x =
          this._initialRotation.x - easeInOutSine(elapsedTime * 0.2);
      }
    }

    // only for STL
    // if (this._bbox && this._model)
    //   this._bbox.rotation.copy(this._model.rotation);
  }
  onToggleTheme(isDarkMode: boolean): void {}
  onToggleDebug(isDebug: boolean): void {
    if (this._bbox) this._bbox.visible = isDebug;
  }

  dispose(): void {
    if (this._model) {
      this._model.parent?.remove(this._model);
      this._material.dispose();
      this._model.clear();
    }
  }
}
